import { useState, useEffect } from 'react';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

/**
 * Calculate time remaining until target date
 */
function calculateTimeRemaining(targetDate: Date): TimeRemaining {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const total = target - now;

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, total };
}

interface CountdownProps {
  /** Target date for the countdown */
  targetDate: Date;
  /** Callback when countdown expires */
  onExpire?: () => void;
}

/**
 * Fullscreen countdown overlay component
 * Displays time remaining until target date
 */
export default function Countdown({ targetDate, onExpire }: CountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(targetDate)
  );

  useEffect(() => {
    // Calculate immediately on mount
    setTimeRemaining(calculateTimeRemaining(targetDate));

    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(targetDate);
      setTimeRemaining(remaining);

      if (remaining.total <= 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, onExpire]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zajadacz-blue"
      role="timer"
      aria-live="polite"
      aria-label="Countdown timer"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Main countdown content */}
      <div className="relative z-10 text-center px-4">
        {/* Title */}
        <h1 className="text-white/90 text-xl sm:text-2xl md:text-3xl font-light tracking-widest uppercase mb-8 sm:mb-12">
          Coming Soon
        </h1>

        {/* Countdown display */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12">
          <TimeUnit value={timeRemaining.days} label="Days" />
          <TimeUnit value={timeRemaining.hours} label="Hours" />
          <TimeUnit value={timeRemaining.minutes} label="Minutes" />
          <TimeUnit value={timeRemaining.seconds} label="Seconds" />
        </div>

        {/* Separator line */}
        <div className="mt-12 sm:mt-16 w-24 h-px bg-white/30 mx-auto" />

        {/* Optional subtitle */}
        <p className="mt-8 text-white/60 text-sm sm:text-base tracking-wide">
          Something exciting is on its way
        </p>
      </div>
    </div>
  );
}

interface TimeUnitProps {
  value: number;
  label: string;
}

/**
 * Individual time unit display (days, hours, minutes, seconds)
 */
function TimeUnit({ value, label }: TimeUnitProps) {
  return (
    <div className="flex flex-col items-center min-w-[80px] sm:min-w-[100px] md:min-w-[120px]">
      <div className="relative">
        {/* Glow effect behind number */}
        <div className="absolute inset-0 bg-white/10 blur-xl rounded-lg" />
        
        {/* Number display */}
        <span
          className="relative block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white tabular-nums"
          style={{ fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace" }}
        >
          {value.toString().padStart(2, '0')}
        </span>
      </div>
      
      {/* Label */}
      <span className="mt-2 sm:mt-3 text-xs sm:text-sm uppercase tracking-[0.2em] text-white/70 font-medium">
        {label}
      </span>
    </div>
  );
}
